import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Typography,
  Button,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import { encrypt } from "../../Encrypdecryp/Crypt";
import JsonEditor from "../../components/JsonEditor";
import PdfPreview from "../../components/PdfPreview";
import * as pdfjsLib from "pdfjs-dist";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate } from "react-router-dom";
import DragAndDrop from "../ViewAndUpload/DragAndDrop";
import FileCard from "../ViewAndUpload/FileCard";
import ProgressBar from "../ViewAndUpload/ProgressBar";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to get auth headers - matching working implementation
const getAuthHeaders = (token) => {
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

const processRawDocuments = async (rawDocs, token) => {
  console.log("Processing raw documents:", rawDocs);
  if (!Array.isArray(rawDocs)) return [];
  
  if (!token) {
    console.error("No token provided to processRawDocuments");
    return [];
  }
  
  const processedDocs = [];
  
  for (const doc of rawDocs) {
    console.log("Processing document:", doc);
    let thumbnail = "/default_thumb.jpg";
    if (doc.thumbnail_path) {
      try {
        const thumbnailResponse = await axios.get(
          `${API_BASE_URL}/bank-account-documents/thumbnails/${doc.thumbnail_path}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            responseType: "blob",
          }
        );
        thumbnail = URL.createObjectURL(thumbnailResponse.data);
        console.log(`Thumbnail fetched successfully for doc ${doc.id}`);
      } catch (err) {
        console.error(`=== THUMBNAIL FETCH ERROR ===`);
        console.error(`Error fetching thumbnail for doc ${doc.id}:`, err);
        console.error('Error response:', err.response?.data);
        console.error('Error status:', err.response?.status);
        console.error('Error headers sent:', err.config?.headers);
        console.error('Request URL:', err.config?.url);
        console.error(`===========================`);
      }
    }

    const mainFile = {
      id: doc.id.toString(),
      name: doc.initial_data?.document_type || doc.finalized_data?.document_type || (doc.pdf_path ? doc.pdf_path.split("/").pop().replace('.pdf', '') : `Document ${doc.id}`),
      docType: doc.initial_data?.document_type || doc.finalized_data?.document_type || "Unknown",
      pdfPath: doc.pdf_path || null,
      thumbnail,
      thumbnail_path: doc.thumbnail_path,
      customer_id: doc.bank_account_opening_id?.toString() || "",
      isSaved: doc.is_confirmed || false,
      extractedData: doc.finalized_data || doc.initial_data || null,
      reference_id: doc.reference_id || null,
      mongo_id: doc.mongo_id || null,
    };
    console.log("Main file created:", mainFile);

    const files = [mainFile];

    if (doc.bank_account_pdf_path) {
      let bankThumbnail = "/default_thumb.jpg";
      if (doc.bank_account_thumbnail_path) {
        try {
          
          const thumbnailResponse = await axios.get(
            `${API_BASE_URL}/bank-account-documents/thumbnails/${doc.bank_account_thumbnail_path}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              responseType: "blob",
            }
          );
          bankThumbnail = URL.createObjectURL(thumbnailResponse.data);
          console.log(`Bank thumbnail fetched successfully for doc ${doc.id}`);
        } catch (err) {
          console.error(`Error fetching bank thumbnail for doc ${doc.id}:`, err);
          console.error('Error response:', err.response?.data);
        }
      }

      const bankFile = {
        id: `${doc.id}-bank`,
        name: "Bank Account Document",
        docType: "Bank Account Document",
        pdfPath: doc.bank_account_pdf_path,
        thumbnail: bankThumbnail,
        thumbnail_path: doc.bank_account_thumbnail_path,
        customer_id: doc.bank_account_opening_id?.toString() || "",
        isSaved: doc.is_confirmed || false,
        extractedData: null,
        reference_id: doc.reference_id ? `${doc.reference_id}-bank` : null,
        mongo_id: doc.mongo_id || null,
      };
      files.push(bankFile);
      console.log("Bank file created:", bankFile);
    }

    const documentName = doc.initial_data?.document_type || `Untitled Document ${doc.id}`;
    processedDocs.push({
      id: doc.id.toString(),
      name: documentName,
      thumbnail,
      files,
    });
  }
  
  return processedDocs;
};

const processBankAccountOpening = async (bankAccountData, token) => {
  console.log("Processing bank_account_opening:", bankAccountData);
  if (!bankAccountData) return [];
  
  if (!token) {
    console.error("No token provided to processBankAccountOpening");
    return [];
  }
  
  const processedDocs = [];
  
  let thumbnail = "/default_thumb.jpg";
  if (bankAccountData.thumbnail_path) {
    try {
      const thumbnailResponse = await axios.get(
        `${API_BASE_URL}/bank-account-opening/thumbnails/${bankAccountData.thumbnail_path}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );
      thumbnail = URL.createObjectURL(thumbnailResponse.data);
      console.log(`Bank account thumbnail fetched successfully`);
    } catch (err) {
      console.error(`Error fetching thumbnail for bank account opening:`, err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
    }
  }

  const mainFile = {
    id: bankAccountData.id.toString(),
    name: "Bank Account Opening Form",
    docType: "Bank Account Opening",
    pdfPath: bankAccountData.pdf_path || null,
    thumbnail,
    thumbnail_path: bankAccountData.thumbnail_path,
    customer_id: bankAccountData.id.toString(),
    isSaved: bankAccountData.is_confirmed || false,
    extractedData: bankAccountData.finalized_data || bankAccountData.initial_data || null,
    reference_id: bankAccountData.reference_id || null,
    mongo_id: bankAccountData.id || null,
    isBankAccountOpening: true,
  };

  const documentName = "Bank Account Opening Form";
  processedDocs.push({
    id: `bank-account-${bankAccountData.id}`,
    name: documentName,
    thumbnail,
    files: [mainFile],
  });
  
  console.log("Bank account opening processed:", processedDocs);
  return processedDocs;
};

const ExistingCustomerDocs = ({
  userRole: propUserRole,
  theme: propTheme,
  truncateFileName = (name) => (name.length > 20 ? `${name.substring(0, 17)}...` : name),
}) => {
  // Get theme from props or default to dark
  const theme = propTheme || "dark";
  
  // Get userRole from props or localStorage
  const [userRole, setUserRole] = useState(propUserRole || "normal");
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMessage, setSearchMessage] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [hoveredDocument, setHoveredDocument] = useState(null);
  const [previewThumbnails, setPreviewThumbnails] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [originalExtractedData, setOriginalExtractedData] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const [pollingProgress, setPollingProgress] = useState(0);
  const [pollingReferenceIds, setPollingReferenceIds] = useState(new Set());
  const [progressComplete, setProgressComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [docType, setDocType] = useState("");
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const fileInputRef = useRef(null);

  // Fetch user role if not provided as prop
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!propUserRole) {
        try {
          const token = localStorage.getItem("authToken");
          if (!token) {
            setUserRole("normal");
            return;
          }
          const response = await axios.get(`${API_BASE_URL}/users/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUserRole(response.data.is_superuser === "True" ? "admin" : "normal");
        } catch (err) {
          console.error("Failed to fetch user role:", err);
          setUserRole("normal");
        }
      }
    };
    fetchUserRole();
  }, [propUserRole]);

  // Function to get all current file names (existing + uploaded)
  const getAllCurrentFileNames = useCallback(() => {
    const existingFileNames = documents.flatMap((doc) =>
      doc.files.map((file) => ({
        name: file.name,
        reference_id: file.reference_id,
      }))
    );

    const uploadedFileNames = uploadedFiles.map((file) => ({
      name: file.name,
      reference_id: file.reference_id,
    }));

    return [...existingFileNames, ...uploadedFileNames];
  }, [documents, uploadedFiles]);

  const isSavedDisabledExisting = (file, index) => {
    return (
      selectedFileType !== "existing" ||
      selectedFileIndex !== index ||
      !isDataChanged
    );
  };

  const isSavedDisabledUploaded = (file, index) => {
    return (
      !file.extractedData || 
      !file.reference_id || 
      (file.isSaved && !isDataChanged) ||
      selectedFileType !== "uploaded" ||
      selectedFileIndex !== index
    );
  };

  const handleError = useCallback((error, defaultMessage) => {
    let errorMessage = defaultMessage;
    if (error.response?.data?.detail) {
      if (Array.isArray(error.response.data.detail)) {
        errorMessage += error.response.data.detail
          .map((d) => `${d.msg} at ${d.loc.join(", ")} (type: ${d.type})`)
          .join("; ");
      } else if (typeof error.response.data.detail === "string") {
        errorMessage += error.response.data.detail;
      } else if (typeof error.response.data.detail === "object") {
        errorMessage += JSON.stringify(error.response.data.detail);
      } else {
        errorMessage += "Unknown detail format";
      }
    } else if (error.response?.data?.message) {
      errorMessage += error.response.data.message;
    } else if (error.message) {
      errorMessage += error.message;
    } else {
      errorMessage += "Unknown error";
    }

    if (error.response?.status === 403) {
      errorMessage = "Access denied: Invalid or expired token. Please log in again.";
      localStorage.removeItem("authToken");
      navigate("/login");
    }

    setError(errorMessage);
    setLoading(false);
    toast.error(errorMessage, {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      style: { 
        background: theme === "dark" ? "#f8d7da" : "#fee2e2", 
        color: "#721c24", 
        borderRadius: "8px", 
        fontSize: "14px" 
      },
    });
  }, [navigate, theme]);

  const fetchStatusData = useCallback(async (referenceId, token) => {
    try {
      console.log(`Fetching status for reference_id: ${referenceId}`);
      const response = await axios.get(
        `${API_BASE_URL}/bank-account-documents/status/${referenceId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Status endpoint response:", response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching status for reference_id: ${referenceId}`, error);
      handleError(error, "Failed to fetch document status: ");
      return null;
    }
  }, [handleError]);

  const fetchAllCustomerData = useCallback(async () => {
    console.log("Fetching all customer data for ID:", customerId);
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No authentication token found in localStorage");
        throw new Error("No authentication token found.");
      }

      console.log("=== FETCH ALL CUSTOMER DATA - TOKEN DEBUG ===");
      console.log(`Token found: ${token.substring(0, 20)}...`);
      console.log("Token length:", token.length);
      console.log("==========================================");

      const params = new URLSearchParams();
      params.append("bank_account_opening_id", customerId);
      const endpoint = `${API_BASE_URL}/bankaccount_documents?${params.toString()}`;
      
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log('Full API response:', response.data);

      const { bank_account_opening, documents: documentsArray } = response.data;

      let allDocuments = [];
      if (bank_account_opening) {
        console.log('Processing bank_account_opening with token');
        const bankAccountDocs = await processBankAccountOpening(bank_account_opening, token);
        allDocuments = [...bankAccountDocs];
      }

      if (Array.isArray(documentsArray) && documentsArray.length > 0) {
        console.log('Processing documents array with token');
        const uploadedDocs = await processRawDocuments(documentsArray, token);
        allDocuments = [...allDocuments, ...uploadedDocs];
      }

      console.log('All processed documents:', allDocuments);
      console.log('Total documents loaded:', allDocuments.length);
      setDocuments(allDocuments);

      if (allDocuments.length > 0 && !selectedDocument) {
        console.log("Auto-selecting first document:", allDocuments[0].id);
        setSelectedDocument(allDocuments[0].id);
      }

      setInitialLoadComplete(true);
      setLoading(false);

    } catch (error) {
      console.error("Error fetching customer data:", error);
      handleError(error, "Failed to load customer data: ");
    }
  }, [customerId, handleError, selectedDocument]);

  useEffect(() => {
    console.log("ExistingCustomerDocs mounted. Customer ID:", customerId);
    console.log("Theme received:", theme);
    
    const token = localStorage.getItem("authToken");
    console.log("Token check on mount:", token ? `Token exists: ${token.substring(0, 20)}...` : "No token found");
    
    if (!token) {
      console.error("No authentication token found in localStorage");
      toast.error("No authentication token found. Please log in.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { 
          background: theme === "dark" ? "#f8d7da" : "#fee2e2", 
          color: "#721c24", 
          borderRadius: "8px", 
          fontSize: "14px" 
        },
      });
      navigate("/login");
      return;
    }

    if (!token.trim() || token === "undefined" || token === "null") {
      console.error("Invalid token format:", token);
      localStorage.removeItem("authToken");
      toast.error("Invalid authentication token. Please log in again.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { 
          background: theme === "dark" ? "#f8d7da" : "#fee2e2", 
          color: "#721c24", 
          borderRadius: "8px", 
          fontSize: "14px" 
        },
      });
      navigate("/login");
      return;
    }

    fetchAllCustomerData();
  }, [customerId, navigate, fetchAllCustomerData, theme]);

  const handleFileSelect = useCallback(
    async (index, documentId) => {
      console.log(`handleFileSelect called with documentId: ${documentId}, index: ${index}`);
      const document = documents.find((d) => d.id === documentId);
      if (!document || !document.files[index]) {
        console.error("Document or file not found:", { documentId, index, documents });
        setSelectedFileIndex(null);
        setSelectedFileType(null);
        setPdfUrl(null);
        setExtractedData(null);
        setOriginalExtractedData(null);
        setDocumentId(null);
        setLoading(false);
        toast.error("The selected file could not be found.", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: { 
            background: theme === "dark" ? "#f8d7da" : "#fee2e2", 
            color: "#721c24", 
            borderRadius: "8px", 
            fontSize: "14px" 
          },
        });
        return;
      }

      setSelectedFileType("existing");
      setSelectedFileIndex(index);
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found.");

        const file = document.files[index];
        console.log("Selected file:", file);

        let pdfUrl = null;
        if (file.pdfPath) {
          const downloadEndpoint = file.isBankAccountOpening 
            ? `${API_BASE_URL}/bank-account-opening/download/${file.pdfPath}`
            : `${API_BASE_URL}/bank-account-documents/download/${file.pdfPath}`;
          
          try {
            const pdfResponse = await axios.get(
              downloadEndpoint,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
                responseType: "blob",
              }
            );
            pdfUrl = URL.createObjectURL(pdfResponse.data);
            console.log("PDF fetched successfully, URL:", pdfUrl);
          } catch (pdfError) {
            console.error(`Failed to fetch PDF for ${file.pdfPath}:`, pdfError);
            throw new Error(`Failed to download PDF for ${file.pdfPath}`);
          }
        } else {
          console.warn("No pdfPath available for file:", file);
          toast.warn("No PDF path available for this file.", {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: { 
              background: theme === "dark" ? "#fff3cd" : "#fefcbf", 
              color: "#856404", 
              borderRadius: "8px", 
              fontSize: "14px" 
            },
          });
        }

        setPdfUrl(pdfUrl);

        let thumbnail = file.thumbnail || "/default_thumb.jpg";
        if (file.thumbnail_path) {
          try {
            const thumbnailEndpoint = file.isBankAccountOpening
              ? `${API_BASE_URL}/bank-account-opening/thumbnails/${file.thumbnail_path}`
              : `${API_BASE_URL}/bank-account-documents/thumbnails/${file.thumbnail_path}`;
            
            console.log(`Fetching thumbnail: ${thumbnailEndpoint}`);
            const thumbnailResponse = await axios.get(
              thumbnailEndpoint,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
                responseType: "blob",
              }
            );
            thumbnail = URL.createObjectURL(thumbnailResponse.data);
            console.log("Thumbnail fetched successfully:", thumbnail);
          } catch (thumbError) {
            console.error("Error fetching thumbnail:", thumbError);
          }
        }

        setPreviewThumbnails([{ name: file.name, thumbnail, fileIndex: index }]);

        const finalData = file.extractedData || null;
        
        console.log("Setting extracted data:", finalData);
        setExtractedData(finalData);
        setOriginalExtractedData(finalData);
        setDocumentId(file.id);
        setIsDataChanged(false);
      } catch (error) {
        console.error("Error in handleFileSelect:", error);
        handleError(error, "Failed to load PDF or extracted data: ");
        setPdfUrl(null);
        setExtractedData(null);
        setOriginalExtractedData(null);
        setDocumentId(null);
      } finally {
        setLoading(false);
      }
    },
    [documents, handleError, theme]
  );

  useEffect(() => {
    console.log("Selected document changed:", selectedDocument);
    if (selectedDocument && (selectedFileIndex === null || selectedFileIndex === undefined) && initialLoadComplete) {
      const document = documents.find((d) => d.id === selectedDocument);
      if (uploadedFiles.length > 0) {
        const firstUploadedIndex = 0;
        setSelectedFileType("uploaded");
        setSelectedFileIndex(firstUploadedIndex);
        setPdfUrl(uploadedFiles[firstUploadedIndex].pdfUrl);
        setExtractedData(uploadedFiles[firstUploadedIndex].extractedData || null);
        setOriginalExtractedData(uploadedFiles[firstUploadedIndex].extractedData || null);
        setDocumentId(uploadedFiles[firstUploadedIndex].documentId || null);
      } else if (document?.files?.length > 0) {
        console.log("Auto-selecting first file for document:", document.id);
        handleFileSelect(0, selectedDocument);
      } else {
        console.log("Document has no existing files. Ready for upload.");
        setSelectedFileIndex(null);
        setSelectedFileType(null);
        setPdfUrl(null);
        setExtractedData(null);
        setOriginalExtractedData(null);
        setDocumentId(null);
      }
    }
  }, [selectedDocument, documents, uploadedFiles, selectedFileIndex, handleFileSelect, initialLoadComplete]);

  const fetchDocuments = async (query, token, limit = 9) => {
    try {
      setLoading(true);
      setSearchMessage("");
      const params = new URLSearchParams();
      params.append("bank_account_opening_id", customerId);
      if (query) params.append("query", encodeURIComponent(query));
      console.log("Fetching documents with params:", params.toString());
      const response = await axios.get(
        `${API_BASE_URL}/bankaccount_documents?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Documents fetched:", response.data);
      
      const { bank_account_opening, documents: documentsArray } = response.data;

      let allDocuments = [];
      if (bank_account_opening) {
        const bankAccountDocs = await processBankAccountOpening(bank_account_opening, token);
        allDocuments = [...bankAccountDocs];
      }

      if (Array.isArray(documentsArray) && documentsArray.length > 0) {
        const uploadedDocs = await processRawDocuments(documentsArray, token);
        allDocuments = [...allDocuments, ...uploadedDocs];
      }

      const validDocuments = allDocuments.filter((d) => d !== null).slice(0, limit);
      setDocuments(validDocuments);
      setSearchMessage(validDocuments.length ? "" : "No valid documents found.");
      setLoading(false);

      if (validDocuments.length > 0 && !selectedDocument) {
        console.log("Auto-selecting first document after fetch:", validDocuments[0].id);
        setSelectedDocument(validDocuments[0].id);
      }
      return validDocuments;
    } catch (error) {
      console.error("Error fetching documents:", error);
      handleError(error, "Unable to fetch documents: ");
      return [];
    }
  };

  const handleDocumentSelect = async (documentId) => {
    console.log(`handleDocumentSelect called with documentId: ${documentId}`);
    setSelectedDocument(documentId);
    setUploadedFiles([]);
    setExtractedData(null);
    setOriginalExtractedData(null);
    setDocumentId(null);
    setLoading(true);
    setError(null);
    setIsDataChanged(false);
  };

  const handleMouseEnter = async (document, index) => {
    setHoveredDocument(document.id);
    const thumbnails = await Promise.all(
      (document.files || []).map(async (file, fileIndex) => {
        let thumbnail = file.thumbnail || "/default_thumb.jpg";
        if (
          !thumbnail.startsWith("blob:") &&
          !thumbnail.startsWith("/default_thumb.jpg") &&
          file.thumbnail_path
        ) {
          const token = localStorage.getItem("authToken");
          if (token) {
            try {
              const thumbnailResponse = await axios.get(
                `${API_BASE_URL}/bank-account-documents/thumbnails/${file.thumbnail_path}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                  responseType: "blob",
                }
              );
              thumbnail = URL.createObjectURL(thumbnailResponse.data);
            } catch (err) {
              console.error("Error fetching thumbnail:", err);
            }
          }
        }
        return { name: file.name, thumbnail, fileIndex };
      })
    );
    setPreviewThumbnails(thumbnails);
  };

  const handleMouseLeave = () => {
    setHoveredDocument(null);
    setPreviewThumbnails([]);
  };

  const handleThumbnailClick = (documentId, fileIndex) => {
    console.log(`handleThumbnailClick called with documentId: ${documentId}, fileIndex: ${fileIndex}`);
    setSelectedDocument(documentId);
    handleFileSelect(fileIndex, documentId);
  };

  const handleUploadedFileSelect = (index) => {
    console.log(`handleUploadedFileSelect called with index: ${index}`);
    setSelectedFileType("uploaded");
    setSelectedFileIndex(index);
    setPdfUrl(uploadedFiles[index].pdfUrl);
    const fileData = uploadedFiles[index].extractedData || null;
    setExtractedData(fileData);
    setOriginalExtractedData(fileData);
    setDocumentId(uploadedFiles[index].documentId || null);
    setIsDataChanged(false);
  };

  const handleSuggestionClick = async (suggestion) => {
    console.log(`handleSuggestionClick called with suggestion: ${suggestion}`);
    setSearchQuery(suggestion);
    setSearchSuggestions([]);
    setIsSearchActive(true);
    const token = localStorage.getItem("authToken");
    if (!token) {
      console.error("No auth token in handleSuggestionClick");
      toast.error("No authentication token found. Please log in again.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { 
          background: theme === "dark" ? "#f8d7da" : "#fee2e2", 
          color: "#721c24", 
          borderRadius: "8px", 
          fontSize: "14px" 
        },
      });
      localStorage.removeItem("authToken");
      navigate("/login");
      return;
    }
    await fetchDocuments(suggestion, token);
  };

  const handleJsonChange = (newData) => {
    console.log("JSON data changed:", newData);
    setExtractedData(newData);
    const isDataUnchanged = JSON.stringify(newData) === JSON.stringify(originalExtractedData);
    setIsDataChanged(!isDataUnchanged);
    setIsEditing(true);
    
    if (selectedFileIndex !== null && selectedFileType === "uploaded" && uploadedFiles[selectedFileIndex]) {
      setUploadedFiles((prev) =>
        prev.map((file, index) =>
          index === selectedFileIndex
            ? { ...file, extractedData: newData, isSaved: false }
            : file
        )
      );
    } else if (selectedFileIndex !== null && selectedFileType === "existing") {
      setDocuments((prev) =>
        prev.map((d) =>
          d.id === selectedDocument
            ? {
                ...d,
                files: d.files.map((f, index) =>
                  index === selectedFileIndex
                    ? { ...f, extractedData: newData, isSaved: false }
                    : f
                ),
              }
            : d
        )
      );
    }
  };

  const handleJsonEditChange = (isEditing) => {
    console.log("JSON editing state changed:", isEditing);
    setIsEditing(isEditing);
    
    if (isEditing && selectedFileIndex !== null) {
      if (selectedFileType === "uploaded" && uploadedFiles[selectedFileIndex]) {
        setUploadedFiles((prev) =>
          prev.map((file, index) =>
            index === selectedFileIndex
              ? { ...file, isSaved: false }
              : file
          )
        );
      } else if (selectedFileType === "existing") {
        setDocuments((prev) =>
          prev.map((d) =>
            d.id === selectedDocument
              ? {
                  ...d,
                  files: d.files.map((f, index) =>
                    index === selectedFileIndex
                      ? { ...f, isSaved: false }
                      : f
                  ),
                }
              : d
          )
        );
      }
    }
  };

  const handleDeleteDocument = async (documentId, referenceId, fileName) => {
    console.log(`handleDeleteDocument called for documentId: ${documentId}, referenceId: ${referenceId}`);
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the document "${fileName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");
          if (!token) throw new Error("No authentication token found.");

          console.log(`Deleting document with reference_id: ${referenceId}`);
          await axios.delete(
            `${API_BASE_URL}/bank-account-documents/${referenceId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          const wasSelected = selectedFileIndex !== null &&
            referenceId === documents.find((d) => d.id === documentId)?.files[selectedFileIndex]?.reference_id;

          setDocuments((prev) => {
            const updated = prev
              .map((d) =>
                d.id === documentId
                  ? {
                      ...d,
                      files: d.files.filter((f) => f.reference_id !== referenceId),
                    }
                  : d
              )
              .filter((d) => d.files.length > 0);
            
            return updated;
          });

          if (wasSelected) {
            setSelectedFileIndex(null);
            setSelectedFileType(null);
            setPdfUrl(null);
            setExtractedData(null);
            setOriginalExtractedData(null);
            setDocumentId(null);
            setIsDataChanged(false);
            setIsEditing(false);

            setTimeout(async () => {
              await fetchAllCustomerData();
              
              const remainingDocs = documents
                .map((d) =>
                  d.id === documentId
                    ? {
                        ...d,
                        files: d.files.filter((f) => f.reference_id !== referenceId),
                      }
                    : d
                )
                .filter((d) => d.files.length > 0);

              if (remainingDocs.length > 0 && remainingDocs[0].files.length > 0) {
                const firstDoc = remainingDocs[0];
                setSelectedDocument(firstDoc.id);
                handleFileSelect(0, firstDoc.id);
              } else if (uploadedFiles.length > 0) {
                setSelectedFileType("uploaded");
                setSelectedFileIndex(0);
                setPdfUrl(uploadedFiles[0].pdfUrl);
                setExtractedData(uploadedFiles[0].extractedData || null);
                setOriginalExtractedData(uploadedFiles[0].extractedData || null);
                setDocumentId(uploadedFiles[0].documentId || null);
              }
            }, 100);
          } else {
            await fetchAllCustomerData();
          }

          setTimeout(() => {
            toast.success("The document has been deleted.", {
              position: "bottom-right",
              autoClose: 1500,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              style: { 
                background: theme === "dark" ? "#d4edda" : "#d1f2eb", 
                color: "#155724", 
                borderRadius: "8px", 
                fontSize: "14px" 
              },
            });
          }, 200);
        } catch (error) {
          console.error("Error deleting document:", error);
          handleError(error, "Failed to delete the document: ");
        }
      }
    });
  };

  const handleDeleteUploadedFile = async (referenceId, fileName, index) => {
    console.log(`handleDeleteUploadedFile called for referenceId: ${referenceId}, index: ${index}`);
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the file "${fileName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        let successMessage = "The file has been removed locally.";

        if (referenceId) {
          try {
            const token = localStorage.getItem("authToken");
            if (!token) throw new Error("No authentication token found.");

            console.log(`Deleting uploaded file with reference_id: ${referenceId}`);
            await axios.delete(
              `${API_BASE_URL}/bank-account-documents/${referenceId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
            successMessage = "The file has been deleted from the server.";
          } catch (error) {
            console.error("Error deleting uploaded file:", error);
            handleError(error, "Failed to delete the document: ");
            return;
          }
        }

        const updatedUploadedFiles = uploadedFiles.filter((f, i) => i !== index);
        setUploadedFiles(updatedUploadedFiles);

        if (selectedFileIndex === index && selectedFileType === "uploaded") {
          if (updatedUploadedFiles.length > 0) {
            const newIndex = 0;
            setSelectedFileType("uploaded");
            setSelectedFileIndex(newIndex);
            setPdfUrl(updatedUploadedFiles[newIndex].pdfUrl);
            setExtractedData(updatedUploadedFiles[newIndex].extractedData || null);
            setOriginalExtractedData(updatedUploadedFiles[newIndex].extractedData || null);
            setDocumentId(updatedUploadedFiles[newIndex].documentId || null);
            setIsEditing(false);
          } else if (documents.length > 0 && documents[0].files.length > 0) {
            setSelectedDocument(documents[0].id);
            handleFileSelect(0, documents[0].id);
          } else {
            setSelectedFileIndex(null);
            setSelectedFileType(null);
            setPdfUrl(null);
            setExtractedData(null);
            setOriginalExtractedData(null);
            setDocumentId(null);
            setIsEditing(false);
          }
        } else if (selectedFileIndex !== null && selectedFileType === "uploaded" && index < selectedFileIndex) {
          setSelectedFileIndex(selectedFileIndex - 1);
        }

        setTimeout(() => {
          toast.success(successMessage, {
            position: "bottom-right",
            autoClose: 1500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: { 
              background: theme === "dark" ? "#d4edda" : "#d1f2eb", 
              color: "#155724", 
              borderRadius: "8px", 
              fontSize: "14px" 
            },
          });
        }, 200);
      }
    });
  };

  const handleFileChange = async (selectedFiles) => {
    console.log("handleFileChange called with files:", selectedFiles);
    if (
      !selectedFiles.length ||
      !Array.from(selectedFiles).every((file) => file.type === "application/pdf")
    ) {
      toast.error("Please upload only PDF files.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { 
          background: theme === "dark" ? "#f8d7da" : "#fee2e2", 
          color: "#721c24", 
          borderRadius: "8px", 
          fontSize: "14px" 
        },
      });
      return;
    }

    if (selectedFiles.length > 10) {
      toast.warning("You can upload up to 10 files at a time.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { 
          background: theme === "dark" ? "#fff3cd" : "#fefcbf", 
          color: "#856404", 
          borderRadius: "8px", 
          fontSize: "14px" 
        },
      });
      return;
    }

    setSimulatedProgress(0);
    setProgressComplete(false);
    setPollingProgress(0);
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");

      const params = new URLSearchParams();
      params.append("bank_account_opening_id", customerId);
      const url = `${API_BASE_URL}/bank-account-documents/upload?${params.toString()}`;
      console.log(`Uploading files to: ${url}`);

      const formData = new FormData();
      Array.from(selectedFiles).forEach((file) => formData.append("file", file));

      const response = await axios.post(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Upload Response:", response.data);
      const referenceIds = Array.isArray(response.data.reference_ids)
        ? response.data.reference_ids
        : [response.data.reference_id];

      const updatedUploadedFiles = await Promise.all(
        Array.from(selectedFiles).map(async (file, index) => {
          const pdfUrl = URL.createObjectURL(file);
          const reader = new FileReader();
          return new Promise((resolve) => {
            reader.onload = async () => {
              const fileData = reader.result;
              const thumbnail = await generateThumbnail(fileData, null, file.name);
              const fileId = `temp-${Date.now()}-${index}`;
              let referenceId = referenceIds[index] || `temp-${Date.now()}-${index}`;
              resolve({
                id: fileId,
                name: file.name,
                docType: docType || "Unknown",
                pdfUrl,
                fileData,
                extractedData: null,
                thumbnail,
                documentId: null,
                customer_id: customerId,
                reference_id: referenceId,
                isSaved: false,
              });
            };
            reader.readAsDataURL(file);
          });
        })
      );

      setUploadedFiles((prev) => {
        const newFiles = [...prev, ...updatedUploadedFiles];
        const newFileIndex = prev.length;
        setSelectedFileType("uploaded");
        setSelectedFileIndex(newFileIndex);
        setPdfUrl(updatedUploadedFiles[0].pdfUrl);
        setExtractedData(updatedUploadedFiles[0].extractedData || null);
        setOriginalExtractedData(updatedUploadedFiles[0].extractedData || null);
        setDocumentId(updatedUploadedFiles[0].documentId || null);
        return newFiles;
      });

      setPollingReferenceIds((prev) => new Set([...prev, ...referenceIds]));
      setLoading(false);

      toast.success("PDF files have been uploaded and are being processed.", {
        position: "bottom-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { 
          background: theme === "dark" ? "#d4edda" : "#d1f2eb", 
          color: "#155724", 
          borderRadius: "8px", 
          fontSize: "14px" 
        },
      });
    } catch (error) {
      console.error("Error uploading files:", error);
      handleError(error, "Failed to upload PDFs: ");
    }
  };

  const generateThumbnail = async (fileData, referenceId, fileName) => {
    try {
      const pdf = await pdfjsLib.getDocument({
        data: atob(fileData.split(",")[1]),
      }).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 0.5 });
      const canvas = document.createElement("canvas");
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const context = canvas.getContext("2d");
      await page.render({ canvasContext: context, viewport }).promise;
      return canvas.toDataURL("image/jpeg");
    } catch (error) {
      console.error("Error generating thumbnail:", error);
      return "/default_thumb.jpg";
    }
  };

  const handleBackToCustomers = async () => {
    console.log("handleBackToCustomers called");
    
    setSelectedDocument("");
    setUploadedFiles([]);
    setSelectedFileIndex(null);
    setSelectedFileType(null);
    setPdfUrl(null);
    setExtractedData(null);
    setOriginalExtractedData(null);
    setDocumentId(null);
    setIsDataChanged(false);
    setSimulatedProgress(0);
    setProgressComplete(false);
    setIsEditing(false);
    navigate("/customer-profiling");
  };

  const handleSaveDocument = useCallback(
    async (file, index) => {
      console.log("handleSaveDocument called for file:", file, "index:", index);
      
      // Determine if this is an existing or uploaded file
      const isExistingFile = selectedFileType === "existing";
      
      // For existing files, get the current extracted data from state
      // For uploaded files, use the file's extracted data
      const dataToSend = isExistingFile && selectedFileIndex === index && extractedData
        ? extractedData
        : file.extractedData;

      if (!dataToSend) {
        console.warn("Cannot save document: missing extracted data");
        toast.warning("Please wait for document processing to complete.", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: { 
            background: theme === "dark" ? "#fff3cd" : "#fefcbf", 
            color: "#856404", 
            borderRadius: "8px", 
            fontSize: "14px" 
          },
        });
        return;
      }

      // Check if reference_id exists
      if (!file.reference_id) {
        console.error("Cannot save document: missing reference_id");
        toast.error("Cannot save document: missing reference ID.", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: { 
            background: theme === "dark" ? "#f8d7da" : "#fee2e2", 
            color: "#721c24", 
            borderRadius: "8px", 
            fontSize: "14px" 
          },
        });
        return;
      }

      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          console.error("No auth token in handleSaveDocument");
          navigate("/login");
          throw new Error("No authentication token found.");
        }

        const requestBody = {
          finalized_data: dataToSend,
        };

        console.log("=== SAVE/UPDATE DOCUMENT (CONFIRM ENDPOINT) ===");
        console.log("Reference ID:", file.reference_id);
        console.log("Request payload:", JSON.stringify(requestBody, null, 2));
        console.log("===============================================");

        // Always use confirm endpoint with reference_id
        await axios.post(
          `${API_BASE_URL}/bank-account-documents/confirm/${file.reference_id}`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Document saved/updated successfully via confirm endpoint");

        // Update the document in state based on file type
        if (selectedFileType === "existing") {
          // Update existing documents
          setDocuments((prev) =>
            prev.map((d) =>
              d.id === selectedDocument
                ? {
                    ...d,
                    files: d.files.map((f, i) =>
                      i === index
                        ? { ...f, extractedData: dataToSend, isSaved: true }
                        : f
                    ),
                  }
                : d
            )
          );
        } else if (selectedFileType === "uploaded") {
          // Update uploaded files
          setUploadedFiles((prev) =>
            prev.map((f, i) =>
              i === index
                ? { ...f, extractedData: dataToSend, isSaved: true }
                : f
            )
          );
        }

        setOriginalExtractedData(dataToSend);
        setIsDataChanged(false);
        setIsEditing(false);

        toast.success("Document has been saved!", {
          position: "bottom-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: { 
            background: theme === "dark" ? "#d4edda" : "#d1f2eb", 
            color: "#155724", 
            borderRadius: "8px", 
            fontSize: "14px" 
          },
        });
      } catch (error) {
        console.error("Error saving document:", error);
        handleError(error, "Failed to save the document: ");
      }
    },
    [selectedFileType, selectedFileIndex, extractedData, selectedDocument, navigate, handleError, theme]
  );

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchSuggestions([]);
      setSearchMessage("");
      setIsSearchActive(false);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found.");

        setLoading(true);
        setSearchMessage("");

        const params = new URLSearchParams();
        params.append("bank_account_opening_id", customerId);
        const response = await axios.get(
          `${API_BASE_URL}/bankaccount_documents?${params.toString()}&query=${encodeURIComponent(
            searchQuery
          )}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Search suggestions response:", response.data);
        const matches = response.data.documents?.flatMap((doc) => [
          doc.initial_data?.document_type,
          doc.finalized_data?.document_type,
        ]).filter(Boolean) || [];
        const suggestions = [...new Set(matches)];
        setSearchSuggestions(suggestions);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching search suggestions:", error);
        handleError(error, "Unable to fetch search suggestions: ");
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, customerId, handleError]);

  useEffect(() => {
    let interval;
    if (
      pollingReferenceIds.size > 0 &&
      simulatedProgress < 100 &&
      uploadedFiles.length > 0
    ) {
      interval = setInterval(() => {
        setSimulatedProgress((prev) => {
          if (prev >= 99) {
            clearInterval(interval);
            return prev;
          }
          return prev + 2;
        });
      }, 100);
    }
    return () => clearInterval(interval);
  }, [pollingReferenceIds.size, uploadedFiles.length, simulatedProgress]);

  useEffect(() => {
    if (uploadedFiles.length === 0) {
      setSimulatedProgress(0);
      setProgressComplete(false);
      setPollingReferenceIds(new Set());
    }
  }, [uploadedFiles.length]);

  useEffect(() => {
    const pollingIntervals = {};
    const pollStatus = async (referenceId) => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found.");

        console.log(`Polling status for reference_id: ${referenceId}`);
        const response = await axios.get(
          `${API_BASE_URL}/bank-account-documents/status/${referenceId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Polling status response for reference_id:", referenceId, response.data);
        const statusData = response.data;
        const progress = statusData.progress_percentage || 0;
        setPollingProgress((prev) => Math.max(prev, progress));

        if (statusData.status === "extracted") {
          let pdfUrl = null;
          let thumbnail = "/default_thumb.jpg";

          if (statusData.pdf_file_path) {
            try {
              const pdfResponse = await axios.get(
                `${API_BASE_URL}/bank-account-documents/download/${statusData.pdf_file_path}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                  responseType: "blob",
                }
              );
              pdfUrl = URL.createObjectURL(pdfResponse.data);
              console.log(`PDF fetched for reference_id ${referenceId}`);
            } catch (err) {
              console.error(`Error fetching PDF for ${statusData.pdf_file_path}:`, err);
            }
          }

          if (statusData.thumbnail_path) {
            try {
              const thumbnailResponse = await axios.get(
                `${API_BASE_URL}/bank-account-documents/thumbnails/${statusData.thumbnail_path}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                  responseType: "blob",
                }
              );
              thumbnail = URL.createObjectURL(thumbnailResponse.data);
              console.log(`Thumbnail fetched for reference_id ${referenceId}`);
            } catch (err) {
              console.error(`Error fetching thumbnail for ${statusData.thumbnail_path}:`, err);
            }
          }

          const updatedUploadedFiles = uploadedFiles.map((file) => {
            if (file.reference_id === referenceId) {
              return {
                ...file,
                extractedData: statusData.extracted_data || null,
                documentId: statusData.mongo_id || file.documentId,
                thumbnail: thumbnail !== "/default_thumb.jpg" ? thumbnail : file.thumbnail,
                pdfUrl: pdfUrl || file.pdfUrl,
                pdfPath: statusData.pdf_file_path || file.pdfPath,
                thumbnail_path: statusData.thumbnail_path || file.thumbnail_path,
                isSaved: statusData.finalized || false,
              };
            }
            return file;
          });

          setUploadedFiles(updatedUploadedFiles);

          if (
            selectedFileIndex !== null &&
            updatedUploadedFiles[selectedFileIndex]?.reference_id === referenceId
          ) {
            const newExtractedData = updatedUploadedFiles[selectedFileIndex].extractedData || null;
            setExtractedData(newExtractedData);
            setOriginalExtractedData(newExtractedData);
            setDocumentId(updatedUploadedFiles[selectedFileIndex].documentId || null);
            if (pdfUrl) {
              setPdfUrl(pdfUrl);
            }
          }

          clearInterval(pollingIntervals[referenceId]);
          setPollingReferenceIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(referenceId);
            return newSet;
          });

          if (!progressComplete && pollingReferenceIds.size === 1) {
            setProgressComplete(true);
            setSimulatedProgress(100);
            setTimeout(() => {
              setSimulatedProgress(0);
              setProgressComplete(false);
            }, 3000);
            toast.success(
              "Documents have been processed successfully. Click Save on each document to confirm.",
              {
                position: "bottom-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: { 
                  background: theme === "dark" ? "#d4edda" : "#d1f2eb", 
                  color: "#155724", 
                  borderRadius: "8px", 
                  fontSize: "14px" 
                },
              }
            );
          }
        }
      } catch (error) {
        console.error("Polling error for reference_id:", referenceId, error);
        clearInterval(pollingIntervals[referenceId]);
        setPollingReferenceIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(referenceId);
          return newSet;
        });
        setPollingProgress(0);
        setSimulatedProgress(0);
        setProgressComplete(false);
        toast.error("Failed to check document status.", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: { 
            background: theme === "dark" ? "#f8d7da" : "#fee2e2", 
            color: "#721c24", 
            borderRadius: "8px", 
            fontSize: "14px" 
          },
        });
      }
    };

    pollingReferenceIds.forEach((referenceId) => {
      pollingIntervals[referenceId] = setInterval(() => pollStatus(referenceId), 5000);
    });

    return () =>
      Object.values(pollingIntervals).forEach((interval) => clearInterval(interval));
  }, [
    pollingReferenceIds,
    uploadedFiles,
    selectedFileIndex,
    progressComplete,
    customerId,
    theme,
  ]);

  const filteredDocuments = isSearchActive
    ? documents
    : documents.filter((d) =>
        (d.name || "").toLowerCase().includes(searchQuery.toLowerCase())
      );

  return (
    <Box className="card" sx={{ width: "100%", mb: 3, p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontFamily: "var(--font-family-heading)", fontWeight: 800, mb: 3 }}>
        Existing Customer Documents
      </Typography>

      <Box sx={{ mb: 3 }}>
        <ProgressBar
          progress={simulatedProgress}
          isProcessing={pollingReferenceIds.size > 0}
          hasFiles={uploadedFiles.length > 0}
        />
        <DragAndDrop
          onFileChange={handleFileChange}
          fileInputRef={fileInputRef}
          promptText="Upload Customer Documents"
          buttonText="Browse Files"
          isExistingTransaction={true}
          disabled={pollingReferenceIds.size > 0}
          existingFiles={getAllCurrentFileNames()}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          mt: 3,
        }}
      >
        <Box sx={{ flex: 1, maxWidth: { md: "50%" }, minHeight: 400 }} className="card">
          <Typography variant="h6" gutterBottom sx={{ fontFamily: "var(--font-family-heading)", fontWeight: 700 }}>
            PDF Preview
          </Typography>
          {pdfUrl ? (
            <PdfPreview pdfUrl={pdfUrl} />
          ) : (
            <Typography sx={{ color: "var(--text-muted)" }}></Typography>
          )}
        </Box>

        <Box sx={{ flex: 1, maxWidth: { md: "50%" } }} className="card">
          <Typography variant="h6" gutterBottom sx={{ fontFamily: "var(--font-family-heading)", fontWeight: 700 }}>
            Extracted Data
          </Typography>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <CircularProgress sx={{ color: "var(--primary-color)" }} />
            </Box>
          ) : error ? (
            <Typography color="error" sx={{ color: "var(--error-color)" }}>{error}</Typography>
          ) : extractedData ? (
            <JsonEditor
              jsonData={extractedData}
              onChange={handleJsonChange}
              isNew={false}
              isAdmin={userRole === "admin"}
              transactionId={selectedDocument}
              documentId={documentId}
              fromCustomerProfiling={false}
              onEditChange={handleJsonEditChange}
            />
          ) : (
            <Typography sx={{ color: "var(--text-muted)" }}></Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontFamily: "var(--font-family-heading)", fontWeight: 700, mb: 2 }}>
          All Customer Documents
        </Typography>

        {loading && !initialLoadComplete ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress sx={{ color: "var(--primary-color)" }} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 2 }}>
              {documents.map((doc) => 
                doc.files.map((file, index) => (
                  <FileCard
                    key={`${doc.id}-${file.id}-${index}`}
                    file={file}
                    index={index}
                    selectedFileIndex={
                      selectedFileType === "existing" && selectedDocument === doc.id ? selectedFileIndex : null
                    }
                    onSelect={() => {
                      setSelectedDocument(doc.id);
                      handleFileSelect(index, doc.id);
                    }}
                    onDelete={() =>
                      handleDeleteDocument(doc.id, file.reference_id, file.name)
                    }
                    onSave={() => handleSaveDocument(file, index)}
                    isSavedDisabled={(f, i) => {
                      return (
                        selectedFileType !== "existing" ||
                        selectedDocument !== doc.id ||
                        selectedFileIndex !== index ||
                        !isDataChanged
                      );
                    }}
                  />
                ))
              )}

              {uploadedFiles.length > 0 &&
                uploadedFiles.map((file, index) => (
                  <FileCard
                    key={`uploaded-${file.id}-${index}`}
                    file={file}
                    index={index}
                    selectedFileIndex={
                      selectedFileType === "uploaded" ? selectedFileIndex : null
                    }
                    onSelect={() => handleUploadedFileSelect(index)}
                    onDelete={() =>
                      handleDeleteUploadedFile(file.reference_id, file.name, index)
                    }
                    onSave={() => handleSaveDocument(file, index)}
                    isSavedDisabled={isSavedDisabledUploaded}
                  />
                ))}

              {documents.length === 0 && uploadedFiles.length === 0 && (
                <Typography sx={{ color: "var(--text-muted)" }}>
                  No files available. Upload documents to get started.
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ExistingCustomerDocs;